import { JarvisWebHttpClient } from '@jarvis/web-http';
import { ValueStoreFacade } from '../../JWeb/facades/ValueStoreFacade';
import { getServices } from '../../../infra/startup';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { IAuthProviderService } from '../../authProviderService';
import { ReservedValueStoreKeys } from '../../JWeb/JWebEnums';
import { Stack } from '../../../core/types/stratus';

/**
 * Possible consent states from Smart-Shared-Services API
 */
export type ConsentState = 'OptedIn' | 'OptedOut' | 'NoSelection';

/**
 * HTTP client for Smart-Shared-Services API
 * Provides fallback consent data when ValueStore lookup fails
 */
export class SmartSharedServicesClient {
  private _jarvisWebHttpInstance: JarvisWebHttpClient;
  private _baseUrl: string;
  private _userUri = '/consents';
  private apiVersion = '/api/2';

  constructor({
    stack,
    authProvider
  }: {
    stack: Stack;
    authProvider: IAuthProviderService;
  }) {
    this._jarvisWebHttpInstance = new JarvisWebHttpClient({
      defaultAuthProvider: authProvider
    });
    this._baseUrl = `${this.getUrl(stack)}${this.apiVersion}`;
  }

  // TODO:In the future, this should be in Native Application Service
  async getApplicationID(): Promise<string | undefined> {
    try {
      const response = await ValueStoreFacade.get({
        keys: [ReservedValueStoreKeys.applicationInstanceId]
      });
      const result = (response as any)?.result[0];

      const appInstanceId = result?.Value || result?.value;
      return appInstanceId;
    } catch (err) {
      console.error('Error getting JWeb Value Store', err);
    }
  }
  /**
   * Fetch user consent data from Smart-Shared-Services API
   * Call the consents api to receive the list of consents associated with user or device. If purposeId is present in query params then the list contains only the first consent.
   * @param userId Optional user identifier
   * @returns Promise resolving to consent data or null on failure
   */

  public async getConsentList(
    purposeIds: string[]
  ): Promise<SmartSharedServicesConsentResponse> {
    // Retrieve the current language
    const { language, country } = getServices().localizationService;

    const deviceId = await this.getApplicationID();

    const params = {
      purposeIdList: purposeIds,
      language,
      country,
      deviceId
    };

    // Request data
    const { data } =
      (await this._jarvisWebHttpInstance.post({
        url: `${this._baseUrl}/consents`,
        data: params
      })) || {};

    internalLogger.debug?.(
      'SmartSharedServicesClient: Fetched consent data:',
      data
    );
    return data as SmartSharedServicesConsentResponse;
  }

  /**
   * Get consent states for specific purpose IDs
   * @param purposeIds Array of purpose IDs to filter by
   * @param options Optional parameters for the API call
   * @returns Array of objects containing purposeId and state
   */
  public async getConsentStatesByPurposeIds(
    purposeIds: string[]
  ): Promise<Array<{ purposeId: string; state: ConsentState }>> {
    // purposeIds won't be sent here since it only accepts one in its filter
    const response = await this.getConsentList(purposeIds);

    const filteredConsents = response
      .filter((consent) => purposeIds.includes(consent.purposeId))
      .map((consent) => ({
        purposeId: consent.purposeId,
        state: consent.state as ConsentState
      }));

    internalLogger.debug?.(
      'SmartSharedServicesClient: Filtered consent states:',
      filteredConsents
    );

    return filteredConsents;
  }

  private getUrl(stack: Stack): string {
    let url;
    switch (stack) {
      case Stack.prod:
        url = `https://api.hpsmart.com`;
        break;
      case Stack.stage:
        url = `https://api.hpsmartstage.com`;
        break;
      case Stack.pie:
        url = `https://api.hpsmartpie.com`;
        break;
      case Stack.dev:
        url = `https://api.hpsmartdev.com`;
        break;
      default:
        url = `https://api.hpsmartpie.com`;
        break;
    }
    return url;
  }
}

/**
 * Response interface for Smart-Shared-Services consent endpoint
 */
export type SmartSharedServicesConsentResponse = {
  state: ConsentState;
  purposeId: string;
  verbiageValue: string;
  deviceId: string;
  resourceId: string;
  tenantId: string;
  purposeResourceId: string;
  purposeVersion: string;
  originType: string;
  originId: string;
  verbiageType: string;
  verbiageLocale: string;
  userResourceId: string;
  programId: string;
  country: string;
  accountId: string;
  processingBasis: string;
  organization: string;
  dateTime: string;
}[];
